import React, { useEffect } from 'react'
import styled from 'styled-components'
import { useField } from 'formik'
import { FormGroup } from 'components'
import Icon from 'components/Icon'
import InfoTooltip from 'components/InfoTooltip'
import colors, { $primary, $gray_600 } from 'utils/colors'
import { SwitchFieldProps } from '../types'

const S = {
    Wrapper: styled.div`
        display: flex;
        align-items: center;
    `,
    FormGroup: styled(FormGroup)`
        margin-bottom: 36px;
    `,
    Switch: styled.div<{ checked: boolean, isDisabled: boolean }>`
        position: relative;
        min-width: 38px;
        width: 38px;
        height: 22px;
        margin-right: 10px;
        border-radius: 99px;
        background-color: ${({ checked }) => (checked ? $primary : 'rgba(24, 28, 33, 0.1)')};
        transition: 0.3s;
        cursor: ${({ isDisabled }) => (isDisabled ? 'auto' : 'pointer')};
        &::after {
            content: '';
            position: absolute;
            top:  4px;
            left: 4px;
            transform: translateX(${({ checked }) => (checked ? 16 : 0)}px);
            width: 14px;
            height: 14px;
            border-radius: 50%;
            background-color: white;
            transition: 0.3s;
        }
    `,
    SmallSwitch: styled.div<{ checked: boolean, isDisabled: boolean }>`
        position: relative;
        min-width: 20px;
        width: 20px;
        height: 10px;
        margin-right: 10px;
        border-radius: 99px;
        background-color: ${({ checked }) => (checked ? $primary : $gray_600)};
        opacity: 0.75;
        transition: 0.3s;
        cursor: ${({ isDisabled }) => (isDisabled ? 'auto' : 'pointer')};
        filter: brightness(${({ isDisabled }) => (isDisabled ? 80 : 100)}%);
        &::after {
            content: '';
            position: absolute;
            top:  2px;
            left: 3px;
            transform: translateX(${({ checked }) => (checked ? 9 : 0)}px);
            width: 6px;
            height: 6px;
            border-radius: 50%;
            background-color: white;
            transition: 0.3s;
        }
    `,
    Icon: styled(Icon)`
        position: absolute;
        top: -3px;
        left: 4px;
    `,
    Label: styled.span<{ isDisabled: boolean }>`
        font-weight: normal;
        font-size: 14px;
        color: ${({ isDisabled }) => (isDisabled ? colors.grayTitleOpacity(0.35) : colors.grayTitle)};
    `,
}

const Switch = ({
    name,
    label,
    helperText,
    checked,
    isDisabled,
    size = 'medium',
    onClick = () => null,
    placement,
    ...props
}: SwitchFieldProps) => {
    const [
        field,,helper
    ] = useField<boolean>({ name })

    useEffect(() => {
        if (checked) {
            helper.setValue(checked)
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [checked])

    const handleClick = () => {
        if (isDisabled) {
            return
        }
        helper.setValue(!field.value)
        helper.setTouched(true)
        onClick()
    }

    return (
        <S.FormGroup
            controlId={`${field.name}-switch`}
            {...props}
        >
            <S.Wrapper>
                {size === 'medium' ? (
                    <S.Switch
                        checked={field.value || checked}
                        isDisabled={isDisabled}
                        onClick={handleClick}
                    >
                        <S.Icon icon="check" color="white" size={14} />
                    </S.Switch>
                ) : (
                    <S.SmallSwitch
                        checked={field.value || checked}
                        isDisabled={isDisabled}
                        onClick={handleClick}
                    />
                )}
                <S.Label isDisabled={isDisabled} className="mr-2">{label}</S.Label>
                {helperText && (
                    <InfoTooltip
                        size={24}
                        text={helperText}
                        placement={placement}
                    />
                )}
            </S.Wrapper>
        </S.FormGroup>
    )
}

export default Switch
